/**
 * Credentials Section Component
 *
 * React implementation of the credentials section renderer.
 * Supports variants: badges, list, banner
 */

import { CredentialsData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface CredentialsProps extends SectionProps<CredentialsData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Credentials({ data, ctx, sectionId, elements }: CredentialsProps) {
  const variant = data.variant || 'badges';

  switch (variant) {
    case 'list':
      return <CredentialsList data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'banner':
      return <CredentialsBanner data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <CredentialsBadges data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// BADGES VARIANT
// =============================================================================

function CredentialsBadges({ data, ctx, elements }: CredentialsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="credentials"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg max-w-2xl mx-auto"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg max-w-2xl mx-auto" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}
        </div>

        {/* Credentials Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {data.credentials?.map((cred, i) => {
            const nameEl = findElement(elements, `credentials.${i}.name`);
            const logoEl = findElement(elements, `credentials.${i}.logo`);

            return (
              <div
                key={i}
                className="p-6 rounded-xl text-center flex flex-col items-center"
                style={{
                  backgroundColor: recipe.colors.background,
                  borderRadius: recipe.borders.cardRadius,
                  boxShadow: recipe.shadows.card,
                }}
              >
                {(cred.logo || logoEl) && (
                  <div className="w-20 h-20 mb-4 flex items-center justify-center">
                    {logoEl ? (
                      <EditableImage
                        element={logoEl}
                        alt={cred.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <img
                        src={cred.logo}
                        alt={cred.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    )}
                  </div>
                )}
                {nameEl ? (
                  <EditableText
                    element={nameEl}
                    as="h3"
                    className="font-semibold text-sm"
                    style={{ color: recipe.colors.text }}
                  />
                ) : (
                  <h3 className="font-semibold text-sm" style={{ color: recipe.colors.text }}>
                    {cred.name}
                  </h3>
                )}
                {cred.issuer && (
                  <p className="text-xs mt-1" style={{ color: recipe.colors.textMuted }}>
                    {cred.issuer}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Trust Statement */}
        {data.trustStatement && (
          <p className="text-center mt-10 text-lg" style={{ color: recipe.colors.textMuted }}>
            {data.trustStatement}
          </p>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// LIST VARIANT
// =============================================================================

function CredentialsList({ data, ctx, elements }: CredentialsProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="credentials"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {(data.eyebrow || eyebrowEl) && (
            <div className="mb-4">
              {eyebrowEl ? (
                <EditableText
                  element={eyebrowEl}
                  as="span"
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                />
              ) : (
                <span
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{ color: recipe.colors.primary }}
                >
                  {data.eyebrow}
                </span>
              )}
            </div>
          )}

          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-3xl md:text-4xl font-bold"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}
        </div>

        {/* Credentials List */}
        <div className="space-y-4">
          {data.credentials?.map((cred, i) => {
            const nameEl = findElement(elements, `credentials.${i}.name`);
            const descEl = findElement(elements, `credentials.${i}.description`);

            return (
              <div
                key={i}
                className="flex items-start gap-4 p-6 rounded-xl"
                style={{
                  backgroundColor: recipe.colors.surface,
                  borderRadius: recipe.borders.cardRadius,
                }}
              >
                {cred.logo ? (
                  <img
                    src={cred.logo}
                    alt={cred.name}
                    className="w-16 h-16 object-contain"
                  />
                ) : (
                  <div
                    className="w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${recipe.colors.primary}15`, color: recipe.colors.primary }}
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    {nameEl ? (
                      <EditableText
                        element={nameEl}
                        as="h3"
                        className="font-semibold"
                        style={{ color: recipe.colors.text }}
                      />
                    ) : (
                      <h3 className="font-semibold" style={{ color: recipe.colors.text }}>
                        {cred.name}
                      </h3>
                    )}
                    {cred.year && (
                      <span className="text-sm" style={{ color: recipe.colors.textMuted }}>
                        {cred.year}
                      </span>
                    )}
                  </div>
                  {cred.issuer && (
                    <p className="text-sm" style={{ color: recipe.colors.primary }}>
                      {cred.issuer}
                    </p>
                  )}
                  {(cred.description || descEl) && (
                    descEl ? (
                      <EditableText
                        element={descEl}
                        as="p"
                        className="text-sm mt-2"
                        style={{ color: recipe.colors.textMuted }}
                      />
                    ) : (
                      <p className="text-sm mt-2" style={{ color: recipe.colors.textMuted }}>
                        {cred.description}
                      </p>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// BANNER VARIANT
// =============================================================================

function CredentialsBanner({ data, ctx, elements }: CredentialsProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');

  return (
    <section
      id="credentials"
      className="py-12"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="p"
            className="text-center text-sm font-medium mb-6 uppercase tracking-wider"
            style={{ color: recipe.colors.textMuted }}
          />
        ) : data.headline ? (
          <p className="text-center text-sm font-medium mb-6 uppercase tracking-wider" style={{ color: recipe.colors.textMuted }}>
            {data.headline}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
          {data.credentials?.map((cred, i) => (
            <div key={i} className="flex items-center justify-center">
              {cred.logo ? (
                <img
                  src={cred.logo}
                  alt={cred.name}
                  className="h-8 md:h-10 object-contain opacity-70 hover:opacity-100 transition-opacity"
                />
              ) : (
                <span className="text-sm font-medium" style={{ color: recipe.colors.textMuted }}>
                  {cred.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Credentials;
